import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Bell, ChevronDown, LogOut, Search, User } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { NAVIGATION } from './navigation';
import { GlobalSearch } from './GlobalSearch';
import { InstallPrompt } from './InstallPrompt';
import { useDemoInfo } from '@/lib/demo';
import { AssistantLauncher } from '@/components/assistant/AssistantLauncher';
import { MobileTabBar, MoreSheet, useBadges } from './MobileNav';

/** Hinweis in der öffentlichen Demo: fiktive Daten, Änderungen gesperrt */
function DemoBanner() {
  const demo = useDemoInfo();
  if (!demo?.enabled) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-900">
      <span>
        <strong>Demo-Modus:</strong> Alle Daten sind fiktiv. Änderungen sind deaktiviert.
      </span>
      <a href="https://mietverwaltungsportal.de" className="font-medium underline">
        Zur Website
      </a>
    </div>
  );
}

const COLLAPSED_KEY = 'mv.sidebar.collapsed';

function loadCollapsed(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

const isActivePath = (pathname: string, to: string) => (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`));

/**
 * Seitenleiste (Desktop). Gruppen lassen sich auf- und zuklappen; der Zustand wird im Browser gemerkt.
 * Die Gruppe der aktuellen Seite ist immer offen; bei zugeklappten Gruppen erscheint die Summe der Zähler im Kopf.
 */
function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { can } = useAuth();
  const badges = useBadges();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<string[]>(loadCollapsed);
  const toggle = (label: string) =>
    setCollapsed((c) => {
      const next = c.includes(label) ? c.filter((x) => x !== label) : [...c, label];
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next));
      } catch {
        /* Speicher nicht verfügbar – Zustand gilt nur für diese Sitzung */
      }
      return next;
    });
  return (
    <nav className="flex h-full flex-col overflow-y-auto bg-brand-900 px-3 pb-6 pt-4 text-slate-300" aria-label="Hauptnavigation">
      <Link to="/" className="mb-6 mt-1 block px-2" onClick={onNavigate} aria-label="Mietverwaltung Portal – zur Übersicht">
        <img src="/brand/logo-light.png" alt="Mietverwaltung Portal – Klar und digital" className="h-auto w-full max-w-[216px]" width={216} height={26} />
      </Link>
      {NAVIGATION.map((group, gi) => {
        const items = group.items.filter((i) => (!i.permission || can(i.permission)) && !(i.hideWithPermission && can(i.hideWithPermission)));
        if (!items.length) return null;
        // Die erste Gruppe (Übersicht/Dashboard) bleibt immer sichtbar
        const collapsible = gi > 0;
        const containsActive = items.some((i) => isActivePath(pathname, i.to));
        const open = !collapsible || containsActive || !collapsed.includes(group.label);
        const groupCount = items.reduce((sum, i) => sum + (i.badgeKey ? badges[i.badgeKey] ?? 0 : 0), 0);
        const listId = `nav-group-${gi}`;
        return (
          <div key={group.label} className={open ? 'mb-4' : 'mb-1'}>
            {collapsible ? (
              <button
                type="button"
                onClick={() => toggle(group.label)}
                aria-expanded={open}
                aria-controls={listId}
                disabled={containsActive}
                title={containsActive ? 'Enthält die aktuelle Seite' : open ? 'Zuklappen' : 'Aufklappen'}
                className="group mb-1 flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400/80 transition-colors hover:bg-white/5 hover:text-slate-200 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-slate-400/80"
              >
                <span className="flex-1 truncate">{group.label}</span>
                {!open && groupCount > 0 && <span className="rounded-full bg-amber-400 px-1.5 text-[10px] font-semibold normal-case tracking-normal text-slate-900 tabular">{groupCount}</span>}
                <ChevronDown className={clsx('h-3.5 w-3.5 shrink-0 transition-transform', !open && '-rotate-90', containsActive && 'opacity-0')} />
              </button>
            ) : (
              <p className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400/80">{group.label}</p>
            )}
            {open && (
              <ul id={listId} className="anim-fade space-y-0.5">
                {items.map((item) => {
                  const count = item.badgeKey ? badges[item.badgeKey] : 0;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === '/'}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          clsx(
                            'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors',
                            isActive ? 'bg-white/15 font-medium text-white' : 'hover:bg-white/5 hover:text-white',
                          )
                        }
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span className="flex-1 truncate">{item.label}</span>
                        {count > 0 && <span className="rounded-full bg-amber-400 px-1.5 text-[11px] font-semibold text-slate-900 tabular">{count}</span>}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function NotificationBell() {
  const { data } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
    retry: false,
  });
  const count = data?.count ?? 0;
  return (
    <Link to="/benachrichtigungen" className="relative rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700" aria-label="Benachrichtigungen">
      <Bell className="h-5 w-5" />
      {count > 0 && (
        <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white tabular">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  useEffect(() => {
    const h = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  if (!user) return null;
  return (
    <div className="relative" ref={ref}>
      <button type="button" className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-100" onClick={() => setOpen((o) => !o)}>
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-800">
          {user.firstName[0]}
          {user.lastName[0]}
        </span>
        <span className="hidden text-left leading-tight lg:block">
          <span className="block text-sm font-medium text-slate-800">
            {user.firstName} {user.lastName}
          </span>
          <span className="block text-xs text-slate-500">{user.role.name}</span>
        </span>
        <ChevronDown className="hidden h-4 w-4 text-slate-400 lg:block" />
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-1 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => { setOpen(false); navigate('/profil'); }}>
            <User className="h-4 w-4" /> Mein Profil & Sicherheit
          </button>
          <InstallPrompt variant="menu" onAction={() => setOpen(false)} />
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
          >
            <LogOut className="h-4 w-4" /> Abmelden
          </button>
        </div>
      )}
    </div>
  );
}

export function AppLayout() {
  const [moreOpen, setMoreOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    setMoreOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);
  // Seitenwechsel-Animation nur beim Wechsel des Bereichs (Unterseiten behalten ihren Zustand)
  const section = location.pathname.split('/')[1] ?? '';

  return (
    <div className="min-h-screen">
      {/* Desktop-Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block">
        <Sidebar />
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 border-b border-slate-200/80 bg-white/85 pt-safe backdrop-blur-xl">
          <div className="flex h-14 items-center gap-2 px-3 sm:px-5">
            <Link to="/" className="shrink-0 lg:hidden" aria-label="Zur Übersicht">
              <img src="/icons/favicon-48.png" alt="" className="h-8 w-8 rounded-lg" width={32} height={32} />
            </Link>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-full border border-slate-200 bg-slate-100/80 px-3 text-left text-sm text-slate-400 hover:border-slate-300 sm:max-w-md lg:rounded-md lg:bg-slate-50"
              aria-label="Suchen"
            >
              <Search className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">
                <span className="sm:hidden">Suchen …</span>
                <span className="hidden sm:inline">Mieter, Adresse, IBAN, Vertrag suchen …</span>
              </span>
              <kbd className="hidden rounded border border-slate-200 bg-white px-1.5 text-[10px] text-slate-500 lg:block">Strg K</kbd>
            </button>
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <NotificationBell />
              <div className="hidden lg:block">
                <UserMenu />
              </div>
            </div>
          </div>
        </header>
        <InstallPrompt variant="banner" />
        <DemoBanner />
        {(user?.mustChangePassword || user?.twoFactorRequired) && location.pathname !== '/profil' && <Navigate to="/profil" replace />}
        {user?.mustChangePassword && location.pathname !== '/profil' && (
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-sm text-amber-900">
            Bitte ändern Sie Ihr temporäres Passwort. <Link to="/profil" className="font-medium underline">Jetzt ändern</Link>
          </div>
        )}
        {user?.twoFactorRequired && location.pathname !== '/profil' && (
          <div className="border-b border-purple-200 bg-purple-50 px-5 py-2 text-sm text-purple-900">
            Für Administratoren ist die Zwei-Faktor-Authentifizierung vorgeschrieben. <Link to="/profil" className="font-medium underline">Jetzt einrichten</Link>
          </div>
        )}
        <main key={section} className="anim-page mx-auto max-w-[1600px] px-3 pb-tabbar pt-4 sm:px-5 sm:pt-5 lg:px-8 lg:pb-24">
          <Outlet />
        </main>
      </div>
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
      <MobileTabBar moreOpen={moreOpen} onMore={setMoreOpen} />
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <AssistantLauncher />
    </div>
  );
}
